import { inject, Injectable, isDevMode, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { rxState } from '@rx-angular/state';
import { setUser as sentrySetUser } from '@sentry/angular';
import {
  catchError,
  combineLatest,
  distinctUntilChanged,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { ADMIN_ROLE_KEY } from '@ess/shared-utils';
import { DEFAULT_LOGGED_IN_ROUTE } from '../enums/routes.enum';
import {
  CurrentUserGQL,
  CurrentUserQuery,
  Membership,
  RegisterUserGQL,
  RegisterUserMutationVariables,
  RegistrationStatusGQL,
  RegistrationStatusQuery,
  UpdateUserGQL,
  UpdateUserMutationVariables,
} from '../graphql/generated';
import { LocaleService } from '../i18n/locale.service';
import { hasCompletedRegistration } from '../utils/registration-status';
import { AnalyticsService } from './analytics.service';
import { FirebaseAuthService } from './firebase-auth.service';
import { ToastersService } from './toasters.service';

type UserServiceState = {
  loading: boolean;
  isRegistered: boolean;
};

@Injectable({ providedIn: 'root' })
export class UserService {
  private readonly router = inject(Router);
  private readonly state = rxState<UserServiceState>();
  private readonly toastersService = inject(ToastersService);

  private readonly authService = inject(FirebaseAuthService);
  private readonly analyticsService = inject(AnalyticsService);

  protected readonly i18n = inject(LocaleService).translation;
  private readonly registerUserGQL = inject(RegisterUserGQL);
  private readonly currentUserGQL = inject(CurrentUserGQL);
  private readonly registrationStatusGQL = inject(RegistrationStatusGQL);
  private readonly updateUserGQL = inject(UpdateUserGQL);

  private readonly currentUserQueryRef = this.currentUserGQL.watch();
  // Only queries the API for a registered & verified user — the CurrentUser
  // query rejects anonymous requests, and an erroring observable would be
  // rethrown by toSignal() during change detection of every consumer
  // (e.g. the /public pages, which are browsed without a token).
  private readonly currentUserData$: Observable<CurrentUserQuery | null> =
    combineLatest([
      this.authService.authGuardData$,
      this.state.select('isRegistered'),
    ]).pipe(
      map(([authData, isRegistered]) => ({
        token: authData.token,
        isVerified: authData.emailVerified,
        isRegistered,
      })),
      distinctUntilChanged(
        (a, b) =>
          a.token === b.token &&
          a.isRegistered === b.isRegistered &&
          a.isVerified === b.isVerified,
      ),
      switchMap(({ token, isRegistered, isVerified }) => {
        const firebaseUser = this.authService.state.get('user');

        if (!token || !isRegistered || !isVerified || firebaseUser == null) {
          // if the user isn't authorized yet, return undefined observable
          sentrySetUser(null);
          this.analyticsService.identifyUser(null);
          return of(null);
        }

        sentrySetUser({
          id: firebaseUser.uid,
        });

        return this.currentUserQueryRef.valueChanges.pipe(
          map((result) => result.data),
          tap((data) => {
            this.analyticsService.identifyUser(data?.me);
            const email = data?.me?.email;
            const fullName = data?.me?.fullName;
            const canSendPersonalData =
              isDevMode() ||
              email?.endsWith('@flowup.cz') ||
              email?.endsWith('@essentialcollege.cz');

            sentrySetUser({
              id: firebaseUser.uid,
              ...(canSendPersonalData
                ? {
                    ...(email && { email }),
                    ...(fullName && { username: fullName }),
                  }
                : {}),
            });
          }),
          catchError((error: unknown) => {
            console.error('Error fetching current user:', error);
            return of(null);
          }),
        );
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

  currentUser = toSignal(this.currentUserData$, { initialValue: null });

  readonly fullName$ = this.currentUserData$.pipe(
    map((data) => data?.me?.fullName || null),
  );

  // #696's full permission system is not built yet; until it is, "admin" is the single
  // role key the API grants admin-only queries against, so the UI gates on the same key
  // the @auth directive already enforces server-side.
  readonly isAdmin$ = this.currentUserData$.pipe(
    map((data) => data?.me?.roles?.includes(ADMIN_ROLE_KEY) ?? false),
  );

  readonly membership$ = this.currentUserData$.pipe(
    map((data) => data?.me?.membership ?? Membership.Free),
  );

  readonly hasActiveMembership$ = this.membership$.pipe(
    map((membership) => membership !== Membership.Free),
  );

  readonly membershipExpirationDate$ = this.currentUserData$.pipe(
    map((data) => data?.me?.membershipExpirationDate || null),
  );

  readonly loading$ = this.state.select('loading');
  readonly isUserRegistered$ = this.state.select('isRegistered');

  readonly locationOptions = signal([
    'Praha',
    'Jihomoravský kraj',
    'Zlínsky kraj',
    'Olomoucký kraj',
    'Moravskoslezský kraj',
    'Kraj Vysočina',
    'Královohradecký kraj',
    'Pardubický kraj',
    'Středočeský kraj',
    'Jihočeský kraj',
    'Plzeňský kraj',
    'Karlovarský kraj',
    'Ústecký kraj',
    'Liberecký kraj',
    'Jiné',
    'Slovensko',
  ]);

  readonly genderOptions$ = of([
    this.i18n.profile.gender.male,
    this.i18n.profile.gender.female,
    this.i18n.profile.gender.preferNotToSay,
  ]);

  constructor() {
    this.initState();
  }

  private initState(): void {
    this.state.set({ loading: false, isRegistered: false });
  }

  // Guard-facing twin of isAdmin$: reads the same role key, but off a settled fetch
  // rather than the live stream, whose first emission is null while the query is still
  // in flight — a guard reading that would bounce a real admin off the page. Cache-first,
  // so it normally costs nothing after registerGuard's own fetch, and fails closed.
  checkIsAdmin$(): Observable<boolean> {
    return this.currentUserGQL.fetch().pipe(
      take(1),
      map((result) => result.data.me?.roles?.includes(ADMIN_ROLE_KEY) ?? false),
      catchError((error: unknown) => {
        console.warn('Error resolving admin role:', error);
        return of(false);
      }),
    );
  }

  // "Registered" means the person completed the registration form, not that a users row
  // exists — an admin invite creates the row up front, and an invited user must still be
  // routed through the form (which claims the row and stamps registrationDate).
  checkIfUserRegistered$(): Observable<boolean> {
    return this.registrationStatusGQL.fetch().pipe(
      take(1),
      map((result) => {
        const isRegistered = hasCompletedRegistration(result.data.me);
        this.state.set({ isRegistered });
        return isRegistered;
      }),
      // A prod deploy ships the UI and the API in parallel from one tag, so for a few
      // minutes this query can hit an API without registrationDate and fail validation.
      // Falling back to the row-existence check keeps that window at the old behaviour
      // instead of bouncing every signed-in user to the registration form.
      catchError(() => this.checkUserRowExists$()),
    );
  }

  private checkUserRowExists$(): Observable<boolean> {
    return this.currentUserGQL.fetch().pipe(
      take(1),
      map((user) => {
        const isRegistered = user.data.me != null;
        this.state.set({ isRegistered });
        return isRegistered;
      }),
      catchError((error: unknown) => {
        console.warn(error);
        return of(false);
      }),
    );
  }

  // What the admin filled into the invite; empty for a user with no row at all (the
  // pre-invite registration flow), whose status query has already failed by the time
  // the register form asks. Cache-first, so it reuses the guard's own fetch.
  registerPrefill$(): Observable<RegistrationStatusQuery['me'] | null> {
    return this.registrationStatusGQL
      .fetch(undefined, { fetchPolicy: 'cache-first' })
      .pipe(
        take(1),
        map((result) => result.data.me ?? null),
        catchError(() => of(null)),
      );
  }

  registerUserData(user: RegisterUserMutationVariables): void {
    this.state.set({ loading: true });
    this.registerUserGQL
      .mutate(user)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.state.set({ loading: false, isRegistered: true });
          this.toastersService.showSuccessMessage(
            this.i18n.toaster.userRegistration.success,
          );
          const redirectUrl =
            this.authService.redirectUrl() || `/${DEFAULT_LOGGED_IN_ROUTE}`;
          this.router
            .navigateByUrl(redirectUrl)
            .then(() => this.authService.setRedirectUrl(null));
        },
        error: (error: unknown) => {
          console.error('Error registering user:', error);
          this.state.set({ loading: false });
          this.toastersService.showErrorMessage(
            this.i18n.toaster.userRegistration.error,
          );
        },
      });
  }

  updateUser(
    user: UpdateUserMutationVariables,
    successMessage: string,
    errorMessage: string,
  ): void {
    this.state.set({ loading: true });

    this.updateUserGQL
      .mutate(user)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.state.set({ loading: false });
          this.toastersService.showSuccessMessage(successMessage);
        },
        error: () => {
          this.state.set({ loading: false });
          this.toastersService.showErrorMessage(errorMessage);
        },
      });
  }

  refetchCurrentUser(): void {
    this.currentUserQueryRef.refetch().catch((error: unknown) => {
      console.error('Error refetching current user:', error);
    });
  }

  clearState(): void {
    this.initState();
  }
}
